import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/solid";
import { HttpError, useOne, useParse, useTranslation, useUpdate } from "@refinedev/core";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { isEmpty } from "lodash";
import { ISimpleEventDetail, ITicketPhasesForm, ITicketsResponseData, Phase,  SupportedLanguage, TicketType } from "interfaces";
import Button from "components/button";
import { SwitchButton } from "components/switchButton";
import { CreateEventStep } from "interfaces";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { TicketPhase } from "./ticketPhase";
import { ConfirmModal } from "components/confirmModal";
import { Tickets } from "./tickets";
import { languageSchema } from "routes/event/schema";
import { Label } from "components/label";

enum Section {
  TICKET_PHASES = "ticket_phases",
  INDIVIDUAL_TICKET = "tickets",
  NONE = "none",
}

const CreateTicketPhaseSchema = (languages: SupportedLanguage[]) => {
  const ticketSchema = z.array(
    z.object({
      category: languageSchema(languages),
      type_name: languageSchema(languages),
      short_description: languageSchema(languages),
      selling_min_qty: z.union([
        z.string().min(1, "This field is required"),
        z
        .number({
          invalid_type_error: "Quantity must be a number",
        })
        .min(0, { message: "Quantity must be equal or greater than 0" }),
      ]),
      selling_max_qty: z.union([
        z.string().min(1, "This field is required"),
        z
        .number({
          invalid_type_error: "Quantity must be a number",
        })
        .min(0, { message: "Quantity must be equal or greater than 0" }),
      ]),
      selling_type: z.string().min(1, "This field is required"),
      price_type: z.string().min(1, "This field is required"),
      original_price: z.union([
        z.string().min(1, "This field is required"),
        z
        .number({
          invalid_type_error: "Quantity must be a number",
        })
        .min(0, { message: "Quantity must be equal or greater than 0" }),
      ]),
      price: z.union([
        z.string().min(1, "This field is required"),
        z
        .number({
          invalid_type_error: "Quantity must be a number",
        })
        .min(0, { message: "Quantity must be equal or greater than 0" }),
      ]),
      sell_from: z.string().min(1, "This field is required"),
      sell_to: z.string().min(1, "This field is required"),
      attendant_template: z.array(
        z.object({
          template_id: z.string(),
          name: z.object({
            en: z.string().optional(),
            vi: z.string().optional()
          }),
        })
      ),
      is_soldout: z.boolean(),
      is_active: z.boolean(),
      ticket_phase: z.union([
        z.string().min(1, "This field is required"),
        z
        .number({
          invalid_type_error: "Phase must be a number",
        })
      ])
    })
  )

  return z.object({
    ticket_phases: z.array(
      z.object({
        name: languageSchema(languages),
        is_active: z.boolean(),
        hide_in_ui: z.boolean(),
      })
    ),
    tickets: ticketSchema,
  });
};

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  submit: () => {}
}

export const CreateTicketPhase = (props: {eventDetailData: ISimpleEventDetail, refetchEventDetail: () => void}) => {

  const parse = useParse();
  const { params } = parse();

  const { data, isFetched, refetch: fetchTickets } =
  useOne<ITicketsResponseData>({
    resource: "content/events",
    id: `${params?.id}/tickets`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(params?.id && params?.step),
    } as any,
  });

  const defaultTicketsData = data?.data ?? null

  return isFetched ? (
    <CreateTicketPhaseForm
      defaultTicketsData={defaultTicketsData}
      fetchTickets={fetchTickets}
      {...props}
    />
  ) : <></>;
}

export const CreateTicketPhaseForm = ({eventDetailData, defaultTicketsData, fetchTickets, refetchEventDetail}: {eventDetailData: ISimpleEventDetail, defaultTicketsData: ITicketsResponseData | null, fetchTickets: () => void, refetchEventDetail: () => void}) => {
  const [openSectionId, setOpenSectionId] = useState<Section>(Section.NONE);
  const [enablePhaseModalConfig, setEnablePhaseModalConfig] = useState(INITIAL_ACTION_MODAL_CONFIG);

  const { mutate: updateMutation } = useUpdate();

  const parse = useParse();
  const { params } = parse();

  const { translate } = useTranslation();

  const methods = useForm<ITicketPhasesForm, HttpError, ITicketPhasesForm>({
    defaultValues: {
      ticket_phases: defaultTicketsData?.ticket_phases ?? [],
      tickets: defaultTicketsData?.tickets ?? [],
      group_tickets: []
    },
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        CreateTicketPhaseSchema(eventDetailData.languages)
      );
      return createResolver(values, context, options);
    },
  });

  const { data: ticketPhaseData, refetch: refetchTicketPhases } = useOne<Phase[], HttpError>({
    resource: `content/events`,
    id: `${eventDetailData.id}/ticket-phases`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(params?.id && params?.step),
    } as any,
  });

  const ticketPhases = ticketPhaseData?.data ?? null;

  useEffect(() => {
    if (ticketPhases) {
      methods.setValue("ticket_phases", (ticketPhases ?? []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketPhases])

  const _toggleActiveTicketPhase = (isActive: boolean) => {
    updateMutation({
      resource: "content/events",
      id: eventDetailData.id,
      meta: {
        URLSuffix: `ticket-phase/${isActive ? "enable" : "disable"}`,
        ignoreResourceWrapper: true
      },
      values: {},
    },
    {
      onSuccess: () => {
        refetchTicketPhases();
        toast.success(`${isActive ? "Active" : "Inactive"} phase successfully"`);
        fetchTickets();
        refetchEventDetail();
        if (!isActive && openSectionId === Section.TICKET_PHASES) {
          setOpenSectionId(Section.NONE);
        }
      },
    });
  };

  const _toggleActiveTicketPhases = (isActive: boolean, ticketType: TicketType) => {
    updateMutation({
      resource: "content/events",
      id: eventDetailData.id,
      meta: {
        URLSuffix: `public-phases/${ticketType}/${isActive ? "publish" : "unpublish"}`,
        ignoreResourceWrapper: true
      },
      values: {},
    },
    {
      onSuccess: () => {
        refetchTicketPhases();
        toast.success(`${isActive ? "Active" : "Inactive"} all ticket phases successfully`);
        refetchEventDetail();
      },
    });
  };

  const _handleSubmitSection = (sectionId: Section) => {
    if (!openSectionId) return setOpenSectionId(sectionId);
    return setOpenSectionId(
      openSectionId === sectionId ? Section.NONE : sectionId
    );
  };

  const _onCloseEnablePhaseModal = () => {
    setEnablePhaseModalConfig(INITIAL_ACTION_MODAL_CONFIG);
  }

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col h-full gap-y-2 sm:gap-y-4">
        <div className="box-container p-0">
          <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 shrink-0 text-sm">
              <h3 className="text-base font-semibold leading-6">
                Ticket phases
              </h3>
              <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                <Label
                  id="ticket-phases"
                  name={translate("eventSetup.phaseAndTicket.attractionTogglePhaseLabel")}
                  hint={translate("eventSetup.phaseAndTicket.attractionTogglePhaseNote")}
                  className="m-0 font-normal"
                />
                <SwitchButton 
                  checked={!isEmpty(methods.watch("ticket_phases") ?? [])}
                  disabled={!isEmpty(methods.watch("ticket_phases") ?? []) && (!isEmpty(methods.watch("tickets")) || !isEmpty(methods.watch("group_tickets")))}
                  onChange={(isCheck) => {
                    if (isCheck) {
                      setEnablePhaseModalConfig({
                        open: true,
                        submit: () => _toggleActiveTicketPhase(true)
                      })
                    } else {
                      _toggleActiveTicketPhase(false)
                    }
                  }} 
                />
              </div>
              <p className="mt-2">* The free event should not be sold in phases</p>
              <p className="font-medium">* Auto-seating does not apply to tickets in phases</p>
            </div>
            {!isEmpty(methods.watch("ticket_phases")) ? (
              <button
                onClick={() => _handleSubmitSection(Section.TICKET_PHASES)}
                type="button"
              >
                {openSectionId === Section.TICKET_PHASES ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            ) : <></>}
          </div>
          {openSectionId === Section.TICKET_PHASES ? (
            <TicketPhase 
              eventDetailData={eventDetailData}
              fetchTicketPhases={refetchTicketPhases}
            />
          ) : <></>}
        </div>

        <div className="box-container p-0">
          <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 shrink-0">
              <h3 className="text-base font-semibold leading-6">
                Individual Tickets
              </h3>
              {!isEmpty(methods.watch("ticket_phases") ?? []) ? (
                <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                  <Label
                    id="public-all-ticket-phases"
                    name={translate(
                      "eventSetup.phaseAndTicket.attractionPublicAllTicketPhases"
                    )}
                    hint={translate(
                      "eventSetup.phaseAndTicket.attractionPublicAllTicketPhasesNote"
                    )}
                    className="m-0 font-normal"
                  />
                  <SwitchButton 
                    checked={eventDetailData.is_publish_all_ticket_phases ?? false} 
                    onChange={(isChecked) => _toggleActiveTicketPhases(isChecked, TicketType.INDIVIDUAL)} 
                  />
                </div>
              ) : <></>}
            </div>
            <button
              onClick={() => _handleSubmitSection(Section.INDIVIDUAL_TICKET)}
              type="button"
            >
              {openSectionId === Section.INDIVIDUAL_TICKET ? (
                <MinusCircleIcon className="w-6 h-6 text-brand-500" />
              ) : (
                <PlusCircleIcon className="w-6 h-6 text-brand-500" />
              )}
            </button>
          </div>
          {openSectionId === Section.INDIVIDUAL_TICKET ? <Tickets eventDetailData={eventDetailData} ticketPhases={ticketPhases ?? []} fetchEventDetail={refetchEventDetail}  /> : <></>}
        </div>

        <div
          className="w-full mt-auto px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2]"
          style={{ boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px" }}
        >
          <div className="flex items-center justify-end gap-x-4">
            <Link to={`/events/attractions/${params?.id}/edit?step=${CreateEventStep.PARTICIPANT_TEMPLATE}`}>
              <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                Back
              </Button>
            </Link>
            <Link
              to={`/events/attractions/${params?.id}/edit?step=${CreateEventStep.ADDITIONAL_SETUP}`}
            >
              <Button
                className={`w-20 disabled:opacity-70`}
                disabled={eventDetailData.current_step < params?.step}
              >
                Next
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <ConfirmModal 
        key="enable-phase"
        title="Do you want to switch to using the phase?"
        content="All tickets will automatically be assigned to the Standard phase when phase-based selling is enabled"
        open={enablePhaseModalConfig.open}
        onConfirm={enablePhaseModalConfig.submit}
        onClose={_onCloseEnablePhaseModal}
      />
    </FormProvider>
  );
};
